import { Azing } from './azing';
import { environment } from './environment';
import { Utils } from './utils';

declare const azing: Azing;

export class Lang {
  public static getUserLangSetting(): string {
    let lang = null;
    try {
      const azingLanguage = Lang.readFromAzingGlobalEnvironment();
      const localStorageLanguage = Lang.readFromLocalStorage();
      const htmlLanguage = Lang.readFromHtml();
      const urlSegmentLanguage = Lang.readFromUrlSegment();
      const urlQueryParameterLanguage = Lang.reduceToSupportedLocales(Utils.getQueryParameter(environment.languageKey));
      const browserLanguage = Lang.readFromBrowser();
      if (azingLanguage) {
        // we use localization that is preset in 2sxc
        lang = azingLanguage;
      } else if (localStorageLanguage) {
        // we can use localization setting from local storage
        lang = localStorageLanguage;
      } else if (htmlLanguage) {
        // we can use localization setting from html tag
        lang = htmlLanguage;
      } else if (urlSegmentLanguage) {
        // we can use localization setting from url segment
        lang = urlSegmentLanguage;
      } else if (urlQueryParameterLanguage) {
        // we can use localization setting from query string
        lang = urlQueryParameterLanguage;
      } else if (browserLanguage) {
        // we can use localization setting from browser
        lang = browserLanguage;
      } else {
        // fallback to default language
        lang = environment.defaultLang;
      }
    } catch (error) {
      lang = environment.defaultLang;
    }

    // Store azing language in localStorage.
    Lang.storeInLocalStorage(lang);

    // Store azing language in Azing global environment variable.
    Lang.storeInAzingGlobalEnvironment(lang);

    return lang;
  }

  /**
   * Read azing language from localStorage, so we can use it in azing tasks.
   */
  public static readFromLocalStorage(): string {
    let language = null;
    try {
      if (typeof (Storage) !== 'undefined') {
        language = window.localStorage.getItem('azing-language');
      }
    } catch {
      // sink
    }
    return language;
  }

  /**
   * Store azing language in localStorage, so we can use it in azing tasks latter.
   */
  private static storeInLocalStorage(language: string): void {
    try {
      if (language) {
        if (typeof (Storage) !== 'undefined') {
          window.localStorage.setItem('azing-language', language);
        }
      }
    } catch {
      // sink
    }
  }

  /**
   * Store azing language in Azing global environment variable, so we can use it in azing tasks latter.
   */
  private static storeInAzingGlobalEnvironment(language: string): void {
    try {
      if (language) {
        ((window as any).azing as Azing).language = language;
      }
    } catch {
      // sink
    }
  }

  private static reduceToSupportedLocales(language: string): string {
    // there is no localization value
    if (!language) { return null; }

    // make it lowercase to be safe
    const languageLowercase = language.toLocaleLowerCase();

    // check is it in list of localization that are available
    if (environment.langs.indexOf(languageLowercase) > -1) {
      return languageLowercase;
    }

    // localization in not in list od known localizations of this app
    return null;
  }

  private static readFromAzingGlobalEnvironment(): string {
    let lang = null;
    try {
      if (azing && azing.language) {
        lang = azing.language;
      }
    } catch (error) {
      // sink
    }
    return Lang.reduceToSupportedLocales(lang);
  }

  /**
   * Read lang from browser settings.
   */
  public static readFromBrowser() {
    const lang = window.navigator.language // works both in Mozilla and chrome
      || navigator.language // works both in Mozilla and chrome
      || (navigator as any).userLanguage // works in IE
      ;
    return Lang.reduceToSupportedLocales(lang);
  }

  /**
   * Read lang from url.
   */
  public static readFromUrlSegment(): string {
    try {
      const urlSegments = window.location.pathname.split('/');
      for (let index = urlSegments.length; index > 0; index--) {
        const lg = Lang.reduceToSupportedLocales(urlSegments[index]);
        if (lg) {
          return lg;
        }
      }
    } catch (error) {
      // sink
    }
    return null;
  }

  /**
   * Read lang from html tag.
   */
  private static readFromHtml(): string {
    let htmlLanguage = null;
    try {
      htmlLanguage = document.getElementsByTagName('html')[0].getAttribute('lang').split('-')[0];
    } catch {
      // sink
    }
    return Lang.reduceToSupportedLocales(htmlLanguage);
  }
}
